import { useState, useMemo, useCallback } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import useResizeObserver from 'use-resize-observer';
import { useTheme } from '@simplywallst/ui-core';
import type { ComputedColumn, StackBarsEntities } from '../StackBars';
import { mapSumPositiveValues, mapSumNegativeValues } from './utils';
import { StackBars, computeColumns } from '../StackBars';
export interface Props {
  entities: StackBarsEntities;
  entitiesFormatted: Record<string, string>;
  height: number;
  keys: string[];
  columnStackColors: string[];
  columnStackKeys: string[];
  initialSelectedColumn?: string;
  onSelectingColumn?: (key: string) => void;
}

export const VerticalStackBarChart = ({
  entities,
  entitiesFormatted,
  height,
  keys,
  columnStackColors,
  columnStackKeys,
  initialSelectedColumn,
  onSelectingColumn,
}: Props) => {
  const theme = useTheme();
  const [selectingKey, setSelectingkey] = useState<string>(
    initialSelectedColumn || keys[0]
  );
  const offsetTop = theme.x5;
  const offsetBottom = theme.x5;

  const { ref, width: rawWidth } = useResizeObserver<HTMLDivElement>();
  const width = rawWidth || 0;

  const sumColumnPosValues = mapSumPositiveValues(keys, entities);
  const sumColumnNegValues = mapSumNegativeValues(keys, entities);

  const max = Math.max(0, ...sumColumnPosValues, ...sumColumnNegValues);
  const min = Math.min(0, ...sumColumnPosValues, ...sumColumnNegValues);

  const xScale = scaleBand({
    domain: keys,
    range: [0, width],
  });

  const yScale = scaleLinear({
    domain: min === 0 && max === 0 ? [0, 1] : [min, max],
    range: [height, offsetTop],
  });

  const colors = columnStackKeys.reduce(
    (acc, key, index) => ({ ...acc, [key]: columnStackColors[index] }),
    {}
  );

  const zero = yScale(0) as number;

  const computedColumns: ComputedColumn[] = useMemo(() => {
    return computeColumns({
      columnKeys: keys,
      barKeys: columnStackKeys,
      entities,
      entitiesFormatted,
      xScale,
      yScale,
      chartWidth: width,
      colors,
    });
  }, [
    colors,
    columnStackKeys,
    entities,
    entitiesFormatted,
    keys,
    width,
    xScale,
    yScale,
  ]);

  const onSelectColumn = useCallback(
    (key: string) => {
      setSelectingkey(key);
      onSelectingColumn?.(key);
    },
    [onSelectingColumn]
  );

  return (
    <div ref={ref}>
      <svg
        width="100%"
        height={height + offsetBottom}
        shapeRendering="crispEdges"
      >
        <rect x="0" y={zero} width="100%" height="1" fill={theme.color.white} />

        <StackBars
          data={computedColumns}
          onSelectColumn={onSelectColumn}
          selectingColumnKey={selectingKey}
          showColumnKey
          height={height}
        />
      </svg>
    </div>
  );
};
